import { fromJS, List } from 'immutable'
import moment from 'moment'
import React, { Component, Fragment } from 'react'
import { Button } from 'semantic-ui-react'
import Api from '~/api'
import DataTable from '../datatable'

const labels = {
  pending: 'Odottaa siirtoa',
  waiting: 'Kesken',
  waiting_approval: 'Odottaa hyväksyntää',
  nobilling: 'Ei laskuteta',
  sent: 'Lähetetty',
  error: 'Virhe lähetyksessä',
  billed: 'Maksettu',
  external: 'Laskutetaan muualla',
}

const filterFields = [
  {
    type: 'group',
    placeholder: 'Status',
    fields: [
      {
        name: 'status',
        type: 'checkboxes',
        label: 'Status',
        options: Object.keys(labels).map(value => ({ value, label: labels[value] })),
      },
    ],
  },
  {
    name: 'send_at',
    type: 'dates',
  },
]

class InvoiceSend extends Component {
  state = {
    loading: true,
    sending: false,
    invoices: List(),
    success: false,
    error: false,
  }

  componentDidMount() {
    Api.get('/invoices')
      .then(({ data }) => {
        let { invoices = [], success, message = 'Jotain meni pieleen' } = data

        if (success) return this.setState({ invoices: fromJS(invoices), loading: false })

        this.setState({
          error: message,
          loading: false,
        })
      })
      .catch(err => {
        console.log(err)
        this.setState({
          error: err.message || 'Jotain meni pieleen',
          loading: false,
        })
      })
  }

  sendInvoices = () => {
    this.setState({
      sending: true,
    })

    Api.post('/invoices', { send: true })
      .then(({ data }) => {
        let { invoices = [], success, message = 'Jotain meni pieleen' } = data

        if (success) return this.setState({ invoices: fromJS(invoices), sending: false, success: true })

        this.setState({
          error: message,
          sending: false,
        })
      })
      .catch(err => {
        console.log(err)
        this.setState({
          error: err.message || 'Jotain meni pieleen',
          sending: false,
        })
      })
  }

  render() {
    let { loading, invoices, error, sending, success } = this.state

    return (
      <div className="invoice-send">
        {error ? <div className="invoice-send-error">{error}</div> : null}

        {success ? <div className="invoice-send-success">Onnistunesti siirretty!</div> : null}
        {loading ? (
          <div className="invoice-send-loader">Ladataan lähetettäviä laskuja...</div>
        ) : !invoices.size ? (
          <div className="invoice-send-empty">Ei lähtettäviä laskuja</div>
        ) : (
          <div className="invoice-send-items">
            <div className="invoice-send-items-title">
              {success ? 'Lähetetty' : 'Lähetetään'} {invoices.size} lasku{invoices.size > 1 ? 'a' : ''}
            </div>
            {invoices.map(i => {
              return (
                <div className="invoice-send-item" key={i.get('id')}>
                  <div className="invoice-send-item-name">{i.get('name')}</div>
                  <div className="invoice-send-item-info">
                    <b>Summa</b>
                    <span>{i.getIn(['data', 'totalSum'])}</span>
                    <b>Tuotteet</b>
                    <span>{i.getIn(['data', 'rows'])}</span>
                    {i.getIn(['data', 'contract_id']) ? (
                      <a
                        href={`https://onlinetechnologygroupoy.pipedrive.com/deal/${i.getIn(['data', 'contract_id'])}`}
                        target="_blank">
                        Avaa Pipedrivessä
                      </a>
                    ) : null}
                  </div>
                </div>
              )
            })}
            <div className="invoice-send-action">
              {sending ? (
                <div className="invoice-send-sending">Siirretään laskutukseen...</div>
              ) : !success ? (
                <Button
                  size="tiny"
                  color="green"
                  content="Siirrä Netvisoriin"
                  onClick={e => e.preventDefault() & this.sendInvoices()}
                />
              ) : null}
            </div>
          </div>
        )}
      </div>
    )
  }
}

export default class InvoiceRoot extends Component {
  state = {
    open: false,
    edit: false,
  }

  render() {
    let { open, id } = this.state
    let currentTime = moment()

    const columns = [
      {
        name: 'name',
        label: 'Nimi',
        id: true,
        sortable: true,
      },
      {
        name: 'status',
        label: 'Status',
        sortable: true,
        render: i => {
          let sendAt = moment(i.get('send_at'))
          let label = labels[i.get('status')] || i.get('status')
          if (i.get('status') === 'error') label = `${label} ${i.getIn(['data', 'errorReason'], '')}`

          let price = parseInt(i.getIn(['data', 'totalSum'], 0))

          let color = 'grey'
          if (i.get('status') === 'pending' && sendAt.isBefore(currentTime)) {
            color = 'blue'
            label = 'Siirretään seuraavaksi'
          }

          if (i.get('status') === 'external') color = 'purple'

          if (i.get('status') === 'error') color = 'red'

          if (i.get('status') === 'sent' || i.get('status') === 'billed') color = 'green'

          if (price < 1) {
            color = 'grey'
            label = 'Ei siirretä'
          }

          return (
            <div className="tags">
              <span className={`tags-item tags-item--${color}`}>{label}</span>
            </div>
          )
        },
      },
      {
        name: 'data.totalSum',
        label: 'Summa',
        sortable: true,
        render: i => `${i.getIn(['data', 'totalSum'], 0)} €`,
      },
      {
        name: 'data.rows',
        label: 'Tuotteita',
        sortable: true,
      },
      {
        name: 'send_at',
        label: 'Lähetetään',
        date: 'DD.MM.YYYY - HH:mm',
        width: 200,
        sortable: true,
        defaultSort: true,
      },
      {
        name: 'created_at',
        label: 'Luotu',
        date: 'DD.MM.YYYY - HH:mm',
        width: 200,
        sortable: true,
        defaultSort: false,
      },
    ]

    return (
      <Fragment>
        <DataTable
          id="invoices"
          entity="invoice"
          title="Laskut"
          sortDir="desc"
          actions={[
            {
              text: 'Vie laskut Netvisoriin',
              icon: open ? 'times' : 'share',
              action: () => this.setState({ open: open === false }),
            },
          ]}
          filters
          filterFields={filterFields}
          context={{ editInvoice: id => this.setState({ edit: id }) }}
          columns={columns}
        />
        {open ? (
          <div className="datatable-action datatable-action--create datatable-action--outside">
            <InvoiceSend />
          </div>
        ) : null}
      </Fragment>
    )
  }
}
